"use client";

import Link from "next/link";
import { SignInLink } from "@/components/auth/sign-in-link";
import { useState } from "react";
import {
  ArrowLeft,
  ChevronRight,
  LogOut,
} from "@/components/icons";
import { Authenticated, Unauthenticated } from "convex/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { NavMenuLinks } from "./nav-links";
import { useNavActive } from "./use-nav-active";
import { NAV_MENUS } from "./nav-data";
import type { NavMenu } from "./types";
import { accountMenuGroups } from "./account-menu-links";
import { useAccountContext } from "./use-account-context";
import { SiteFeedbackDialog } from "@/components/feedback/site-feedback-dialog";
import { MobileMenuButton } from "./mobile-menu-button";

export function MobileNavigation({
  onSignOut,
  initiallyOpen = false,
}: {
  onSignOut: () => void;
  initiallyOpen?: boolean;
}) {
  const isActive = useNavActive();
  const [menuOpen, setMenuOpen] = useState(initiallyOpen);
  const [openSection, setOpenSection] = useState<NavMenu | null>(null);

  const handleOpenChange = (open: boolean) => {
    setMenuOpen(open);
    if (!open) setOpenSection(null);
  };
  const closeMenu = () => handleOpenChange(false);

  return (
    <Dialog open={menuOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <MobileMenuButton />
      </DialogTrigger>
      <DialogContent phoneLayout="custom" className="left-auto right-0 top-0 flex h-[100dvh] max-h-[100dvh] w-full max-w-full translate-x-0 translate-y-0 flex-col rounded-none border-border bg-card p-4 sm:max-w-[380px]">
        <MenuSheetHeader
          openSection={openSection}
          onBack={() => setOpenSection(null)}
        />

        <nav aria-label="Main" className="mt-2 flex-1 overflow-y-auto">
          {openSection ? (
            <NavMenuLinks
              menu={openSection}
              isActive={isActive}
              onLinkSelect={closeMenu}
            />
          ) : (
            <MenuSectionList onOpenSection={setOpenSection} />
          )}
        </nav>

        {!openSection && <MobileNavFooter closeMenu={closeMenu} onSignOut={onSignOut} />}
      </DialogContent>
    </Dialog>
  );
}

function MenuSheetHeader({
  openSection,
  onBack,
}: {
  openSection: NavMenu | null;
  onBack: () => void;
}) {
  return (
    <DialogHeader className="flex-row items-center">
      {openSection && (
        <IconButton variant="quiet" size="compact" aria-label="Back to menu" onClick={onBack}>
          <ArrowLeft />
        </IconButton>
      )}
      <DialogTitle>
        {openSection ? openSection.label : "Menu"}
      </DialogTitle>
    </DialogHeader>
  );
}

function MenuSectionList({
  onOpenSection,
}: {
  onOpenSection: (menu: NavMenu) => void;
}) {
  return (
    <ul className="flex flex-col">
      {NAV_MENUS.map((menu) => (
        <li key={menu.label} className="border-b border-border">
          <button
            type="button"
            onClick={() => onOpenSection(menu)}
            className="flex min-h-12 w-full items-center justify-between px-3 text-left text-base text-foreground"
          >
            {menu.label}
            <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

function MobileAuthenticatedFooter({
  closeMenu,
  onSignOut,
}: {
  closeMenu: () => void;
  onSignOut: () => void;
}) {
  const { isAdmin } = useAccountContext();
  const groups = accountMenuGroups(isAdmin);

  return (
    <>
      {groups.map((group) => (
        <div key={group[0]?.href} className="flex flex-col border-t border-border py-1">
          {group.map(({ href, icon: Icon, label }) => (
            <Link
              key={href}
              href={href}
              onClick={closeMenu}
              className="flex min-h-11 items-center gap-2 px-3 text-sm text-muted-foreground hover:text-foreground"
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </Link>
          ))}
        </div>
      ))}
      <Button
        variant="destructive-quiet"
        className="w-full justify-start"
        onClick={() => {
          closeMenu();
          onSignOut();
        }}
        size="default"
      >
        <LogOut className="size-4" aria-hidden />
        Log out
      </Button>
    </>
  );
}

function MobileNavFooter({
  closeMenu,
  onSignOut,
}: {
  closeMenu: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="mt-4 space-y-2 border-t border-border pt-4">
      <Authenticated>
        <MobileAuthenticatedFooter closeMenu={closeMenu} onSignOut={onSignOut} />
      </Authenticated>

      <Unauthenticated>
        <div className="flex gap-2">
          <Button variant="outline" asChild className="flex-1" size="default">
            <SignInLink href="/sign-in" onClickCapture={closeMenu}>Log in</SignInLink>
          </Button>
          <Button variant="primary" asChild className="flex-1" size="default">
            <SignInLink href="/sign-up" onClickCapture={closeMenu}>Sign up</SignInLink>
          </Button>
        </div>
      </Unauthenticated>
      <SiteFeedbackDialog variant="row" />
    </div>
  );
}
